// Bone-pose interpolator: lerp position, slerp quaternion. Drives CharacterView.

import * as THREE from 'three';
import type { CharacterSnapshot } from '../sim/SimState';

const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();
const _va = new THREE.Vector3();
const _vb = new THREE.Vector3();

export function lerpBone(
  prev: CharacterSnapshot | null,
  curr: CharacterSnapshot,
  boneIndex: number,
  alpha: number,
  outPos: THREE.Vector3,
  outQuat: THREE.Quaternion
): void {
  const o = boneIndex * 7;
  const c = curr.bones;
  if (!prev || prev.bones.length !== c.length) {
    outPos.set(c[o]!, c[o + 1]!, c[o + 2]!);
    outQuat.set(c[o + 3]!, c[o + 4]!, c[o + 5]!, c[o + 6]!);
    return;
  }
  // alpha=1 (clamp from GameLoop's substep-overrun branch) lets us skip the
  // slerp entirely — the curr bone is the answer. alpha=0 is rare in steady
  // state but cheap to handle. Removing slerp on these edges saves 30 ops per
  // bone per affected frame; output is bit-identical because slerp(t∈{0,1})
  // returns the endpoint quaternion unchanged.
  if (alpha >= 1) {
    outPos.set(c[o]!, c[o + 1]!, c[o + 2]!);
    outQuat.set(c[o + 3]!, c[o + 4]!, c[o + 5]!, c[o + 6]!);
    return;
  }
  const p = prev.bones;
  if (alpha <= 0) {
    outPos.set(p[o]!, p[o + 1]!, p[o + 2]!);
    outQuat.set(p[o + 3]!, p[o + 4]!, p[o + 5]!, p[o + 6]!);
    return;
  }
  _va.set(p[o]!, p[o + 1]!, p[o + 2]!);
  _vb.set(c[o]!, c[o + 1]!, c[o + 2]!);
  outPos.lerpVectors(_va, _vb, alpha);
  _qa.set(p[o + 3]!, p[o + 4]!, p[o + 5]!, p[o + 6]!);
  _qb.set(c[o + 3]!, c[o + 4]!, c[o + 5]!, c[o + 6]!);
  outQuat.copy(_qa).slerp(_qb, alpha);
}
